import { useState, useCallback } from 'react';
import { DollarSign } from 'lucide-react';
import { useLaborPrices } from '@/hooks/useSettings';

interface PricingStepProps {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

interface PriceRow {
  serviceName: string;
  basePrice: string;
  saved: boolean;
}

const DEFAULT_SERVICES: PriceRow[] = [
  { serviceName: 'Troca de óleo', basePrice: '', saved: false },
  { serviceName: 'Alinhamento', basePrice: '', saved: false },
  { serviceName: 'Balanceamento', basePrice: '', saved: false },
  { serviceName: 'Troca de pastilha', basePrice: '', saved: false },
  { serviceName: 'Diagnóstico geral', basePrice: '', saved: false },
];

const PLACEHOLDERS: Record<string, string> = {
  'Troca de óleo': '80',
  Alinhamento: '120',
  Balanceamento: '60',
  'Troca de pastilha': '150',
  'Diagnóstico geral': '100',
};

export function PricingStep({ onNext, onBack, onSkip }: PricingStepProps) {
  const { create, isCreating } = useLaborPrices();
  const [rows, setRows] = useState<PriceRow[]>(DEFAULT_SERVICES);
  const [saving, setSaving] = useState(false);

  const updateRow = useCallback((index: number, value: string) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, basePrice: value } : r)),
    );
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, { serviceName: '', basePrice: '', saved: false }]);
  }, []);

  const updateServiceName = useCallback((index: number, value: string) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, serviceName: value } : r)),
    );
  }, []);

  const handleSaveAndContinue = useCallback(async () => {
    setSaving(true);
    try {
      const toSave = rows.filter(
        (r) => r.serviceName.trim() && r.basePrice && Number(r.basePrice) > 0 && !r.saved,
      );
      for (const row of toSave) {
        await create({
          serviceName: row.serviceName,
          basePrice: Number(row.basePrice),
        });
      }
      setRows((prev) =>
        prev.map((r) =>
          r.serviceName.trim() && r.basePrice && Number(r.basePrice) > 0
            ? { ...r, saved: true }
            : r,
        ),
      );
      onNext();
    } finally {
      setSaving(false);
    }
  }, [rows, create, onNext]);

  const filledCount = rows.filter(
    (r) => r.serviceName.trim() && r.basePrice && Number(r.basePrice) > 0,
  ).length;

  return (
    <div className="flex flex-1 flex-col px-6 py-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
          <DollarSign size={20} className="text-amber-700 dark:text-amber-300" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Tabela de Preços</h2>
          <p className="text-sm text-muted-foreground">
            Defina o preço base da mão de obra para cada serviço
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="flex-1">
              {i < DEFAULT_SERVICES.length ? (
                <span className="text-sm font-medium">{row.serviceName}</span>
              ) : (
                <input
                  type="text"
                  value={row.serviceName}
                  onChange={(e) => updateServiceName(i, e.target.value)}
                  placeholder="Nome do serviço"
                  maxLength={200}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-muted-foreground">R$</span>
              <input
                type="number"
                value={row.basePrice}
                onChange={(e) => updateRow(i, e.target.value)}
                min={0}
                step="0.01"
                placeholder={PLACEHOLDERS[row.serviceName] ?? '0'}
                disabled={row.saved}
                className="w-28 rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
              />
            </div>
            {row.saved && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Salvo</span>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="mt-3 self-start text-sm text-blue-600 dark:text-blue-400 hover:underline"
      >
        + Adicionar outro serviço
      </button>

      <p className="mt-4 text-xs text-muted-foreground">
        Modificadores por tipo de veículo (nacional, importado, luxo) disponíveis em{' '}
        <span className="font-medium">Configurações &gt; Mão de Obra</span>.
      </p>

      {/* Nav buttons */}
      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={onBack}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          Voltar
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={onSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Pular por agora
          </button>
          <button
            onClick={handleSaveAndContinue}
            disabled={saving || isCreating || filledCount === 0}
            className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Salvando...' : `Salvar ${filledCount > 0 ? `(${filledCount})` : ''} e continuar`}
          </button>
        </div>
      </div>
    </div>
  );
}
